import { useEffect, useState } from "react";
import { Bell, MessageCircle, Store, UserPlus, Camera, Star } from "lucide-react";
import { SwipeAlert } from "@/components/SwipeAlert";
import { api, errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Mail } from "lucide-react";
import { refreshUnread } from "@/lib/useUnread";

const ICONS = { business_reply: Store, review_comment: MessageCircle, photo_comment: Camera, new_follower: UserPlus, show_recap: Star };

export function NotificationList({ kind, empty }) {
  const [items, setItems] = useState(null);
  const [clearing, setClearing] = useState(false);
  useEffect(() => {
    api.get("/notifications", { params: { kind } }).then((r) => {
      setItems(r.data);
      if (r.data.some((n) => !n.read)) api.post("/notifications/read", { kind }).then(refreshUnread).catch(() => {});
    }).catch(() => setItems([]));
  }, [kind]);
  const removeOne = async (id) => {
    const prev = items;
    setItems((list) => list.filter((n) => n.id !== id));
    try { await api.delete(`/notifications/${id}`); } catch (e) { setItems(prev); toast.error(errorMessage(e)); }
  };
  const clearAll = async () => {
    setClearing(true);
    try { await api.delete("/notifications", { params: { kind } }); setItems([]); refreshUnread(); toast.success("Alerts cleared"); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setClearing(false); }
  };
  if (!items) return null;
  if (!items.length) return <p data-testid={`notifications-empty-${kind}`} className="mt-8 text-sm text-slate-500 rounded-2xl border border-dashed border-[#26334D] p-10 text-center">{empty}</p>;
  return (
    <div data-testid={`notifications-list-${kind}`} className="mt-6 space-y-2">
      <div className="flex items-center justify-between pb-1">
        <p className="text-xs text-slate-500">Swipe or tap ✕ to remove an alert</p>
        <button data-testid={`notifications-clear-all-${kind}`} onClick={clearAll} disabled={clearing}
          className="text-sm font-semibold text-[#FF7A7A] hover:text-[#FF3B6B] px-3 py-2 rounded-full hover:bg-[#FF3B6B]/10 transition-colors">
          {clearing ? "Clearing…" : "Clear all"}
        </button>
      </div>
      {items.map((n) => <SwipeAlert key={n.id} n={n} Icon={ICONS[n.type] || Bell} onRemove={removeOne} />)}
    </div>
  );
}

const EMAIL_LABELS = {
  owner_new_review: ["New reviews of my business", "Owner"], owner_traveler_reply: ["Traveler replies on my business reviews", "Owner"],
  traveler_business_reply: ["Business replies to my reviews", "Traveler"], traveler_review_reply: ["Other replies on my reviews", "Traveler"],
  show_reminders: ["Show reminders the day before", "Everyone"],
};

function EmailPrefs() {
  const [prefs, setPrefs] = useState(null);
  useEffect(() => { api.get("/notifications/email-prefs").then((r) => setPrefs(r.data)).catch(() => {}); }, []);
  const set = async (k, on) => {
    try { const { data } = await api.patch("/notifications/email-prefs", { [k]: on }); setPrefs(data); toast.success(on ? "Email turned on" : "Email turned off"); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  if (!prefs) return null;
  return (
    <section data-testid="email-prefs" className="mt-8 rounded-2xl bg-[#151C2C] border border-[#26334D] p-5">
      <p className="text-sm font-semibold flex items-center gap-2"><Mail className="h-4 w-4 text-[#FFB800]" /> Daily summary email</p>
      <p className="text-xs text-slate-400 mt-0.5">One email a day with everything you switched on. In-app notifications always arrive right away.</p>
      <div className="mt-4 space-y-3">
        {Object.keys(prefs).map((k) => (
          <label key={k} className="flex items-center justify-between gap-4 text-sm">
            <span>{EMAIL_LABELS[k]?.[0] || k} <span className="text-[10px] uppercase tracking-wider text-slate-500 ml-1">{EMAIL_LABELS[k]?.[1]}</span></span>
            <Switch data-testid={`email-pref-${k}`} checked={prefs[k]} onCheckedChange={(on) => set(k, on)} />
          </label>
        ))}
      </div>
    </section>
  );
}

export default function Notifications() {
  return (
    <main data-testid="notifications-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="text-4xl font-extrabold tracking-tight flex items-center gap-3"><Bell className="h-7 w-7 text-[#FF3B6B]" /> Notifications</h1>
      <EmailPrefs />
      <NotificationList kind="personal" empty="Nothing yet. Replies, photo comments and new followers will show up here." />
    </main>
  );
}
